/**
 * Single place that maps the backend consent payload onto the view model the
 * Consent page renders. The page itself never reads a raw API key.
 *
 * Contract: POST /api/v1/bgv/tenant/claim -> { success, status, message, data }
 * where `data` carries the request metadata plus a DPDP consent notice whose
 * body is a list of *typed* section blocks. The block `type` is the extension
 * point: an unrecognised type degrades to a best-effort render rather than
 * throwing, so a new notice_key can ship without a frontend release.
 */
import { formatText, setDate } from "../../../Utils/helper";
import { CONSENT } from "../../../Utils/Strings";

export const SECTION_TYPE = {
  PROSE: "prose",
  DATA_TABLE: "dataTable",
  BULLETS: "bullets",
  KEY_VALUE: "keyValue",
  DECLARATION: "declaration",
  UNKNOWN: "unknown"
};

/* Backend `type` -> our renderer key. Anything absent lands on UNKNOWN. */
const TYPE_MAP = {
  prose: SECTION_TYPE.PROSE,
  data_table: SECTION_TYPE.DATA_TABLE,
  bullets: SECTION_TYPE.BULLETS,
  keyvalue: SECTION_TYPE.KEY_VALUE,
  key_value: SECTION_TYPE.KEY_VALUE,
  declaration: SECTION_TYPE.DECLARATION
};

const isObject = (val) => val !== null && typeof val === "object" && !Array.isArray(val);

const asArray = (value) => {
  if (Array.isArray(value)) return value;
  if (value === null || value === undefined || value === "") return [];
  return [value];
};

const isBlank = (value) => value === null || value === undefined || value === "";

const uniq = (list) => Array.from(new Set(list));

const isDev = process.env.NODE_ENV === "development";

/* The envelope is exactly one level deep. The direct-object path lets tests and
   any future caller hand us the inner payload straight. */
const readRoot = (payload) => {
  if (isObject(payload?.data)) return payload.data;
  return isObject(payload) ? payload : {};
};

const columnLabel = (key) =>
  CONSENT.TABLE_COLUMNS[key] || formatText(String(key)) || String(key);

/* setDate yields the literal "Invalid Date" on unparseable input, so fall back
   to the raw backend value instead of showing that to the holder. */
const dateLabel = (value) => {
  if (isBlank(value)) return null;
  const formatted = setDate(value);
  return formatted === "Invalid Date" ? value : formatted;
};

/* `footer.last_updated` is date-only ("2026-08-04"), which JS parses as UTC
   midnight -- setDate would render it as the previous evening east of UTC. Pin
   it to local midnight and drop the time, which is meaningless here anyway. */
const dateOnlyLabel = (value) => {
  if (isBlank(value)) return null;
  const parsed = new Date(`${String(value).slice(0, 10)}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleDateString("en-US", { day: "numeric", month: "short", year: "numeric" });
};

/* ---------- inline runs ---------- */

const SAFE_SCHEMES = ["http:", "https:", "mailto:"];

/* Notice text is backend-supplied content rendered on a public, unauthenticated
   page, so only vet schemes get to be a real anchor. */
const isSafeHref = (href) => {
  try {
    return SAFE_SCHEMES.includes(new URL(String(href), window.location.origin).protocol);
  } catch {
    return false;
  }
};

const toRun = (item) => {
  if (isObject(item)) {
    const text = isBlank(item.text) ? item.href : item.text;
    if (isBlank(text)) return null;
    if (isBlank(item.href) || !isSafeHref(item.href)) return { kind: "text", text: String(text) };
    return { kind: "link", href: String(item.href), text: String(text) };
  }
  if (isBlank(item)) return null;
  return { kind: "text", text: String(item) };
};

/**
 * A section body mixes plain strings with { href, text } link objects, and the
 * two intents are not distinguishable from the shape alone: ["... at ", {link},
 * "."] is one sentence with an inline anchor, while ["A.", "B."] is two
 * paragraphs. Rule: a link never breaks a paragraph; a string starts a new one
 * only when the previous item was also a string.
 */
const segmentBody = (body) => {
  const items = asArray(body);
  /* Explicit paragraphs, should the backend ever nest them. */
  if (items.length && Array.isArray(items[0])) {
    return items.map((group) => asArray(group).map(toRun).filter(Boolean)).filter((p) => p.length);
  }

  const paragraphs = [];
  let previousWasString = false;
  items.forEach((item) => {
    const run = toRun(item);
    const isString = !isObject(item);
    if (run) {
      if (!paragraphs.length || (isString && previousWasString)) paragraphs.push([run]);
      else paragraphs[paragraphs.length - 1].push(run);
    }
    previousWasString = isString;
  });
  return paragraphs;
};

/* ---------- section builders ---------- */

const toEntries = (items) =>
  asArray(items)
    .filter(isObject)
    .map((item) => ({ label: item.label ?? null, value: item.value ?? null }))
    .filter((entry) => !isBlank(entry.label) || !isBlank(entry.value));

const toCell = (row, key) => {
  const value = row[key];
  if (Array.isArray(value)) {
    const tags = value
      .map((item, index) =>
        isObject(item)
          ? { id: item.id ?? `t-${index}`, label: item.label ?? item.id ?? "", mandatory: item.mandatory === true }
          : { id: `t-${index}`, label: String(item), mandatory: false }
      )
      .filter((tag) => !isBlank(tag.label));
    return tags.length ? { kind: "tags", tags } : { kind: "empty" };
  }
  if (isBlank(value)) return { kind: "empty" };
  if (typeof value === "boolean") return { kind: "text", text: value ? "Yes" : "No" };
  return { kind: "text", text: String(value) };
};

const toDataTable = (section, sectionKey) => {
  const columnKeys = asArray(section.columns).map(String);
  const consentColumnKey = columnKeys.includes("consent") ? "consent" : null;
  return {
    columns: columnKeys.map((key) => ({ key, label: columnLabel(key) })),
    consentColumnKey,
    rows: asArray(section.rows)
      .filter(isObject)
      .map((row, index) => {
        const purposeId = isBlank(row.purpose_id) ? null : String(row.purpose_id);
        /* accepted_elements is "<purpose_id>.<data_element_id>", one per element
           the row grants under that purpose -- not the bare purpose_id. */
        const elementIds = asArray(row.data_elements)
          .filter(isObject)
          .map((element) => (isBlank(element.id) ? null : String(element.id)))
          .filter((id) => !isBlank(id));
        return {
          /* The row index is always part of the key: two rows may share a
             purpose_id, and keying the checkbox map on the bare id would wire
             their checkboxes together. The receipt sends the bare id, deduped. */
          key: `${sectionKey}:${index}:${purposeId ?? "row"}`,
          purposeId,
          elementIds,
          mandatory: row.mandatory === true,
          label: row.purpose ?? row.category ?? null,
          cells: columnKeys.reduce((acc, key) => {
            if (key !== consentColumnKey) acc[key] = toCell(row, key);
            return acc;
          }, {})
        };
      })
  };
};

const toAcknowledgements = (section, sectionKey) =>
  asArray(section.acknowledgements)
    .filter(isObject)
    .map((ack, index) => {
      const ackId = isBlank(ack.id) ? null : String(ack.id);
      return {
        key: `${sectionKey}:${index}:${ackId ?? "ack"}`,
        ackId,
        mandatory: ack.mandatory === true,
        text: isBlank(ack.text) ? "" : String(ack.text)
      };
    })
    .filter((ack) => ack.text);

const toSection = (section, index) => {
  if (!isObject(section)) return null;

  const id = isBlank(section.id) ? null : String(section.id);
  const key = `s${index}-${id ?? index}`;
  const rawType = isBlank(section.type) ? null : String(section.type);
  const type = TYPE_MAP[rawType] || SECTION_TYPE.UNKNOWN;
  const base = { key, id, type, heading: isBlank(section.heading) ? null : String(section.heading) };

  switch (type) {
    case SECTION_TYPE.PROSE: {
      const paragraphs = segmentBody(section.body);
      return paragraphs.length ? { ...base, paragraphs } : null;
    }
    case SECTION_TYPE.DATA_TABLE: {
      const table = toDataTable(section, key);
      return table.columns.length ? { ...base, ...table } : null;
    }
    case SECTION_TYPE.BULLETS: {
      const items = asArray(section.items).filter((item) => !isObject(item) && !isBlank(item)).map(String);
      return items.length ? { ...base, items } : null;
    }
    case SECTION_TYPE.KEY_VALUE: {
      const entries = toEntries(section.items);
      const paragraphs = segmentBody(section.body);
      return entries.length || paragraphs.length ? { ...base, paragraphs, entries } : null;
    }
    case SECTION_TYPE.DECLARATION: {
      const acknowledgements = toAcknowledgements(section, key);
      const paragraphs = segmentBody(section.body);
      return acknowledgements.length || paragraphs.length
        ? { ...base, paragraphs, acknowledgements }
        : null;
    }
    default: {
      /* Best effort so a new block type still shows its text instead of
         vanishing silently. */
      if (isDev) console.warn(`[consent] unhandled notice section type: ${rawType}`);
      const paragraphs = segmentBody(section.body);
      const items = asArray(section.items).filter((item) => !isObject(item) && !isBlank(item)).map(String);
      return paragraphs.length || items.length
        ? { ...base, rawType, paragraphs, items }
        : null;
    }
  }
};

const toNotice = (raw) => {
  const notice = raw.notice;
  if (!isObject(notice)) return null;
  const body = isObject(notice.body) ? notice.body : {};
  const sections = asArray(body.sections).map(toSection).filter(Boolean);
  if (!sections.length) return null;

  const footer = isObject(body.footer) ? body.footer : {};
  return {
    key: isBlank(notice.notice_key) ? null : String(notice.notice_key),
    /* The legal version of the notice -- NOT body.schema_version, which happens
       to hold the same number today. The receipt must carry this one. */
    version: isBlank(notice.version) ? null : notice.version,
    contentHash: isBlank(notice.content_hash) ? null : String(notice.content_hash),
    title: notice.title || body.title || null,
    footer: {
      lastUpdated: dateOnlyLabel(footer.last_updated),
      versionLabel: isBlank(footer.version_label) ? null : String(footer.version_label)
    },
    sections
  };
};

/* ---------- lifecycle gate ---------- */

/**
 * The page no longer interprets backend statuses; the adapter hands it a gate.
 * The full status enum is not published, and the two failure modes cost very
 * different amounts: landing on REVIEW for an odd-but-live link is recoverable
 * (the submit call fails and surfaces its message), while showing "Link
 * expired" for a good link is a dead end with no retry. So an unrecognised
 * status resolves to "review". Widening this is a one-line change.
 */
export const GATE_RULES = [
  { test: /EXPIRED|REVOKED|CANCELL?ED|INVALID/, gate: "dead", reason: "expired" },
  { test: /CONSENT_(GIVEN|GRANTED)|APPROVED|ACCEPTED|CONSENTED|COMPLETED/, gate: "done" },
  { test: /DECLINED|REJECTED|REFUSED|WITHDRAWN/, gate: "done" }
];

export const resolveGate = (raw) => {
  /* Strict false only: an absent flag must not read as a dead link. */
  if (raw.notice === null) return { gate: "dead", gateReason: "expired" };

  const haystack = [raw.consent_status, raw.coarse_status]
    .filter((value) => !isBlank(value))
    .map((value) => String(value).toUpperCase())
    .join(" ");

  /* Rule order is the precedence: expiry beats completion beats review. */
  for (const rule of GATE_RULES) {
    if (rule.test.test(haystack)) return { gate: rule.gate, gateReason: rule.reason || null };
  }

  if (isDev && haystack && !/AWAITING_CONSENT|LINK_SENT/.test(haystack)) {
    console.warn(`[consent] unrecognised status pair, defaulting to review: ${haystack}`);
  }
  return { gate: "review", gateReason: null };
};

/* ---------- public API ---------- */

export const normalizeConsent = (payload) => {
  const raw = readRoot(payload);
  const notice = toNotice(raw);
  const sections = notice?.sections || [];
  console.log(raw);
  
  /* Flattened, in notice order, so gating and the receipt never walk the tree. */
  const purposes = sections
    .filter((section) => section.type === SECTION_TYPE.DATA_TABLE && section.consentColumnKey)
    .flatMap((section) => section.rows)
    .map(({ key, purposeId, elementIds, mandatory, label }) => ({ key, purposeId, elementIds, mandatory, label }));

  const acknowledgements = sections
    .filter((section) => section.type === SECTION_TYPE.DECLARATION)
    .flatMap((section) => section.acknowledgements);

  const pkg = isObject(raw.package) ? raw.package : {};

  return {
    requestId: isBlank(raw.request_id) ? null : raw.request_id,
    profileType: isBlank(raw.profile_type) ? null : String(raw.profile_type),
    ...resolveGate(raw),
    requester: { name: isBlank(raw.landlord_name) ? null : String(raw.landlord_name) },
    holder: { name: isBlank(raw.candidate_name) ? null : String(raw.candidate_name) },
    meta: [{ label: CONSENT.META.REQUESTED_ON, value: dateLabel(raw.created_at) }].filter(
      (item) => item.value
    ),
    checks: {
      packageId: isBlank(pkg.id) ? null : pkg.id,
      items: asArray(pkg.checks).filter((item) => !isBlank(item)).map(String)
    },
    notice,
    purposes,
    acknowledgements
  };
};

/**
 * Proof of exactly what was consented to, sent to SUBMIT_HOLDER_CONSENT.
 * accepted_elements is "<purpose_id>.<data_element_id>", one entry per data
 * element under each purpose the holder ticked -- not the bare purpose_id.
 */
export const buildConsentReceipt = (consent, purposeConsents = {}, ackConsents = {}, token, accepted) => ({
  token,
  accepted,
  content_hash: consent?.notice?.contentHash ?? null,
  accepted_elements: uniq(
    (consent?.purposes || [])
      .filter((purpose) => purposeConsents[purpose.key] && purpose.purposeId)
      .flatMap((purpose) => (purpose.elementIds || []).map((elementId) => `${purpose.purposeId}.${elementId}`))
  ),
  acknowledgements: uniq(
    (consent?.acknowledgements || [])
      .filter((ack) => ackConsents[ack.key] && ack.ackId)
      .map((ack) => ack.ackId)
  )
});

/* The send-OTP response contract is not finalised, so this one stays tolerant
   of several key spellings until it is. */
const pickAny = (source, keys) => {
  if (!isObject(source)) return null;
  for (const key of keys) {
    if (!isBlank(source[key])) return source[key];
  }
  return null;
};

export const normalizeOtpInfo = (payload) => {
  const raw = readRoot(payload);
  return {
    channel: pickAny(raw, ["otp_channel", "channel", "sent_via", "medium"]),
    destination: pickAny(raw, [
      "masked_destination", "masked_contact", "masked_email", "masked_phone",
      "masked_phone_number", "destination", "sent_to", "email", "phone_number"
    ]),
    expiresIn: Number(pickAny(raw, ["otp_expires_in", "expires_in", "expiry_in_minutes", "otp_expiry"])) || 0.5
  };
};

export const readApiMessage = (error, fallback) =>
  error?.response?.data?.message ||
  error?.response?.data?.detail ||
  error?.response?.data?.error ||
  error?.message ||
  fallback;

/* HTTP 404/410 means "this link is dead"; a dead *status* on a 200 is handled
   by resolveGate above. */
export const isDeadLinkError = (error) => [404, 410].includes(error?.response?.status);
